import React, { useEffect } from 'react'
import { Field } from 'react-final-form'
import { injectIntl } from 'react-intl'
import { List } from 'immutable'
import { FormHelperText, FormControlLabel } from '@material-ui/core'
import { connect } from 'react-redux'
import getValidator, { VALIDATOR_IS_EMPTY } from 'validation'
import Checkbox from 'components/common/Checkbox'
import { PURPLE } from 'constants/colors'
import SelectList from 'components/common/SelectList'
import { Flex } from 'components/common/styles'
import { fetchLookupTablesSource } from 'reducers/jobInputsAdmin/reducer'
import messages from '../../translate'
import {
  CheckboxContainer,
  ParametersContainer,
  ParametersHeader,
} from '../JobWorkflowForm.styles'
import {
  DeletionReasonParametersProps,
  InputLookupTableSource,
} from './DeletionReasonParameters.types'
import { DELETION_REASON_FIELDS } from '../constants'

function DeletionReasonParameters({
  intl: { formatMessage },
  submitting,
  values,
  fetchLookupTablesSource,
  inputLookupTablesSource,
  onTextAreaClick,
}: DeletionReasonParametersProps) {
  useEffect(() => {
    if (
      values.needs_deletion_reason_settings?.type &&
      [
        DELETION_REASON_FIELDS.LOOKUP,
        DELETION_REASON_FIELDS.LOOKUP_AND_TEXTAREA,
      ].includes(
        values.needs_deletion_reason_settings?.type as DELETION_REASON_FIELDS,
      )
    ) {
      fetchLookupTablesSource()
    }
  }, [values.needs_deletion_reason_settings?.type])

  return (
    <ParametersContainer>
      <ParametersHeader>{formatMessage(messages.parameters)}</ParametersHeader>
      <Flex direction="column">
        <Field
          name="needs_deletion_reason_settings.type"
          validate={getValidator([VALIDATOR_IS_EMPTY])}
          render={({ input, meta }) => (
            <CheckboxContainer>
              <FormControlLabel
                label={formatMessage(messages.textArea)}
                disabled={submitting}
                control={
                  <Checkbox
                    checked={
                      input.value === DELETION_REASON_FIELDS.TEXTAREA ||
                      input.value === DELETION_REASON_FIELDS.LOOKUP_AND_TEXTAREA
                    }
                    color={PURPLE}
                    onChange={() => {
                      switch (input.value) {
                        case DELETION_REASON_FIELDS.LOOKUP:
                          input.onChange(
                            DELETION_REASON_FIELDS.LOOKUP_AND_TEXTAREA,
                          )
                          break
                        case DELETION_REASON_FIELDS.TEXTAREA:
                          input.onChange(null)
                          break
                        case DELETION_REASON_FIELDS.LOOKUP_AND_TEXTAREA:
                          input.onChange(DELETION_REASON_FIELDS.LOOKUP)
                          break
                        default:
                          input.onChange(DELETION_REASON_FIELDS.TEXTAREA)
                      }
                    }}
                  />
                }
              />
              {meta.touched && meta.error && (
                <FormHelperText error>
                  {formatMessage({ id: 'deletion_reason_error' })}
                </FormHelperText>
              )}
            </CheckboxContainer>
          )}
        />

        <Field
          name="needs_deletion_reason_settings.type"
          validate={getValidator([VALIDATOR_IS_EMPTY])}
          render={({ input, meta }) => (
            <CheckboxContainer>
              <FormControlLabel
                label={formatMessage(messages.lookupSelect)}
                disabled={submitting}
                control={
                  <Checkbox
                    checked={
                      input.value === DELETION_REASON_FIELDS.LOOKUP ||
                      input.value === DELETION_REASON_FIELDS.LOOKUP_AND_TEXTAREA
                    }
                    color={PURPLE}
                    onChange={() => {
                      switch (input.value) {
                        case DELETION_REASON_FIELDS.LOOKUP:
                          input.onChange(null)
                          onTextAreaClick()
                          break
                        case DELETION_REASON_FIELDS.TEXTAREA:
                          input.onChange(
                            DELETION_REASON_FIELDS.LOOKUP_AND_TEXTAREA,
                          )
                          break
                        case DELETION_REASON_FIELDS.LOOKUP_AND_TEXTAREA:
                          input.onChange(DELETION_REASON_FIELDS.TEXTAREA)
                          onTextAreaClick()
                          break
                        default:
                          input.onChange(DELETION_REASON_FIELDS.LOOKUP)
                      }
                    }}
                  />
                }
              />
              {meta.touched && meta.error && (
                <FormHelperText error>
                  {formatMessage({ id: 'deletion_reason_error' })}
                </FormHelperText>
              )}
            </CheckboxContainer>
          )}
        />

        {(values.needs_deletion_reason_settings?.type ===
          DELETION_REASON_FIELDS.LOOKUP ||
          values.needs_deletion_reason_settings?.type ===
            DELETION_REASON_FIELDS.LOOKUP_AND_TEXTAREA) && (
          <>
            <Field
              name="needs_deletion_reason_settings.table"
              type="select"
              required
              validate={getValidator([VALIDATOR_IS_EMPTY])}
              render={({ input, meta }) => (
                <SelectList
                  required
                  label={formatMessage(messages.lookupTable)}
                  onChange={(value: { item: { id: number } }) =>
                    input.onChange(value.item.id)
                  }
                  list={inputLookupTablesSource}
                  value={input.value}
                  disabled={submitting}
                  map={{
                    index: 'id',
                    text: 'title',
                  }}
                  controlError={
                    meta.touched &&
                    meta.error &&
                    formatMessage({ id: 'invalid_empty_value' })
                  }
                />
              )}
            />
            <Field
              name="needs_deletion_reason_settings.column"
              type="select"
              validate={getValidator([VALIDATOR_IS_EMPTY])}
              render={({ input, meta }) => (
                <SelectList
                  required
                  label={formatMessage(messages.lookupColumn)}
                  onChange={(value: { item: { key: string } }) =>
                    input.onChange(value.item.key)
                  }
                  list={List(
                    inputLookupTablesSource.find(
                      el =>
                        el.id === values.needs_deletion_reason_settings?.table,
                    )?.columns || [],
                  )}
                  value={input.value}
                  disabled={
                    submitting || !values.needs_deletion_reason_settings?.table
                  }
                  map={{
                    index: 'key',
                    text: 'title',
                  }}
                  controlError={
                    meta.touched &&
                    meta.error &&
                    formatMessage({ id: 'invalid_empty_value' })
                  }
                />
              )}
            />
          </>
        )}
      </Flex>
    </ParametersContainer>
  )
}

export default connect(
  (state: { getIn: (path: Array<string>) => InputLookupTableSource[] }) => ({
    inputLookupTablesSource: state.getIn([
      'jobInputsAdmin',
      'lookupTablesSource',
    ]) as InputLookupTableSource[],
  }),
  {
    fetchLookupTablesSource,
  },
)(injectIntl(DeletionReasonParameters))
